import styles from './StatusChip.module.scss';
import WarningIcon from '@mui/icons-material/WarningAmberOutlined';
import ErrorIcon from '@mui/icons-material/ErrorOutlineOutlined';
import DoneIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { CHIP_TYPE, TOOLTIP_POSITION, CHIP_SIZE } from '@types';
import { PropsWithChildren } from 'react';
import {
	Text,
	Tooltip,
	Caption,
	Label,
	Grid,
} from '@components'
import CloseIcon from '@mui/icons-material/Close';

interface ChipProps {
	text: string | JSX.Element;
	type?: CHIP_TYPE;
	showIcon?: boolean;
	CustomIcon?: React.ComponentType<{ [key: string]: string }>;
	customIconColor?: string;
	iconOnly?: boolean;
	rounded?: boolean;
	collapse?: boolean;
	size?: CHIP_SIZE;
	stretch?: boolean;
	centerText?: boolean;
	noWrap?: boolean;
	title?: string;
	showCloseButton?: boolean;
	actionButtons?: (null | {
		text: string;
		icon?: React.ComponentType<{ [key: string]: string }>;
		onClick: () => void;
	})[];
	onClose?: () => void;
	isToast?: boolean;
}

export const StatusChip = (props: PropsWithChildren<ChipProps>) => {
	const {
		text,
		type = CHIP_TYPE.INFO,
		showIcon = false,
		CustomIcon = null,
		customIconColor = '',
		iconOnly = false,
		rounded = false,
		collapse = false,
		size = CHIP_SIZE.LARGE,
		stretch = false,
		centerText = false,
		noWrap = false,
		children,
		title,
		showCloseButton,
		actionButtons = [],
		onClose,
		isToast
	} = props;
	const getTypeClass = () => {
		switch (type) {
		case CHIP_TYPE.ERROR:
			return styles.error;
		case CHIP_TYPE.SUCCESS:
			return styles.success;
		case CHIP_TYPE.WARN:
			return styles.warn;
		case CHIP_TYPE.NEUTRAL:
			return styles.neutral;
		case CHIP_TYPE.BRAND:
			return styles.brand;
		default:
			return styles.info;
		}
	}
	const getSizeClass = () => {
		switch (size) {
		case CHIP_SIZE.SMALL:
			return styles.small;
		case CHIP_SIZE.MEDIUM:
			return styles.medium;
		case CHIP_SIZE.LARGE:
		default:
			return;
		}
	}
	const renderIcon = () => {
		if (CustomIcon !== null) {
			return <CustomIcon data-testid={'@InfoChip-CustomIcon'} className={styles.icon} color={customIconColor} />
		}
		switch (type) {
		case CHIP_TYPE.ERROR:
			return <ErrorIcon data-testid={'@InfoChip-ErrorIcon'} className={styles.icon} />;
		case CHIP_TYPE.SUCCESS:
			return <DoneIcon data-testid={'@InfoChip-SuccessIcon'} className={styles.icon} />;
		case CHIP_TYPE.WARN:
			return <WarningIcon data-testid={'@InfoChip-WarnIcon'} className={styles.icon} />;
		case CHIP_TYPE.NEUTRAL:
		case CHIP_TYPE.BRAND:
			return <></>;
		default:
			return <InfoIcon data-testid={'@InfoChip-InfoIcon'} className={styles.icon} />;
		}
	}

	const renderTitle = () => {
		if (!title) return null;
		let color = 'var(--txt-info)';
		switch (type) {
		case CHIP_TYPE.ERROR:
			color = 'var(--txt-error)';
			break;
		case CHIP_TYPE.SUCCESS:
			color = 'var(--txt-success)';
			break;
		case CHIP_TYPE.WARN:
			color = 'var(--txt-warning)';
			break;
		case CHIP_TYPE.BRAND:
			color = 'var(--brand-orange-default)';
			break;
		default:
		}
		return <Label color={color}>{title}</Label>
	}

	const renderText = () => {
		if (size === CHIP_SIZE.SMALL) {
			return <Caption>{text}</Caption>
		}
		return <Text>{text}</Text>
	}

	const getColumnsCount = () => {
		let qty = 1;
		if (showIcon) qty++;
		if (showCloseButton || actionButtons?.[0]) qty++
		return qty;
	}

	const getColumnTemplate = () => {
		let str = '';
		if (showIcon) str = size === CHIP_SIZE.SMALL ? '12px' : '20px';
		str = str.concat(noWrap ? ' minmax(0, max-content)' : ' auto');
		if (showCloseButton || actionButtons?.[0]) str = str.concat(' max-content')
		return str;
	}

	const getActionButtonClassName = () => {
		let className = `${styles.actionButton}`
		switch (type) {
		case CHIP_TYPE.SUCCESS:
			className = className.concat(` ${styles.actionButtonSuccess}`);
			break;
		case CHIP_TYPE.WARN:
			className = className.concat(` ${styles.actionButtonWarn}`);
			break;
		case CHIP_TYPE.ERROR:
			className = className.concat(` ${styles.actionButtonError}`);
			break;
		default:
			className = className.concat(` ${styles.actionButtonInfo}`);
		}
		return className;
	}

	return (
		<>
			{iconOnly &&
				<Tooltip position={TOOLTIP_POSITION.TOP} text={text}>
					<div data-testid={'@InfoChip-IconOnly'} className={`${styles.iconOnlyContainer} ${getSizeClass()} ${getTypeClass()}`}>
						{renderIcon()}
					</div>
				</Tooltip>
			}
			{
				!iconOnly &&
				<div
					data-testid={'@InfoChip'}
					className={`
					${styles.container} 
					${getTypeClass()} 
					${getSizeClass()} 
					${rounded ? styles.isRounded : ''} 
					${collapse ? styles.collapse : ''} 
					${stretch ? styles.stretch : ''}
					${centerText && styles.centerText}
					${noWrap ? styles.noWrap : ''}
					`}
					role={isToast ? 'alert' : 'status'}
				>
					<Grid
						columns={getColumnsCount()}
						columnTemplate={getColumnTemplate()}
						gap={getColumnsCount() === 1 ? '0' : '8px'}
						alignItems={'center'}
					>
						{
							showIcon &&
								<span className={styles.iconContainer}>
									{renderIcon()}
								</span>
						}
						<div className={styles.content}>
							{renderTitle()}
							{renderText()}
							{children !== undefined && children}
							{
								actionButtons?.length > 1 ?
									<div className={styles.actionButtonContainer}> {
										actionButtons.map((btn, index) => {
											if (index === 0 || !btn) return null;
											const Icon = actionButtons?.[index]?.icon
											return <button
												key={index}
												className={getActionButtonClassName()}
												onClick={btn.onClick}>
												{Icon && <Icon />}
												{btn.text}
											</button>
										})}
									</div>
									: null
							}
						</div>
						{
							showCloseButton && <button
								onClick={onClose}
								className={`${styles.closeButton} ${getActionButtonClassName()}`}
								aria-label={'Toggle Toast Button'}
								tabIndex={0}
							>
								<CloseIcon />
							</button>
						}
						{
							!showCloseButton && actionButtons?.[0] !== null ?
								actionButtons.map((btn, index) => {
									if (index !== 0 || !btn) return null;
									const Icon = actionButtons?.[index]?.icon
									/*
										*** Temporary solution ***
										Discussed with Paddy and he'll
										add creating this type of buttons
										to his backlog.
									*/
									return <button
										key={index}
										className={getActionButtonClassName()}
										onClick={btn.onClick}>
										{Icon && <Icon />}
										{btn.text}
									</button>
								}) : null
						}
					</Grid>
				</div>
			}
		</>
	);
}

export const InfoChip = (props: PropsWithChildren<ChipProps>) => {
	return (
		<StatusChip
			type={CHIP_TYPE.INFO}
			showIcon
			customIconColor={'var(--func-blue-dark)'}
			{...props}
		/>
	)
}

export const NeutralChip = (props: PropsWithChildren<ChipProps>) => {
	return (
		<StatusChip
			type={CHIP_TYPE.NEUTRAL}
			showIcon
			customIconColor={'var(--chip-neutral)'}
			{...props}
		/>
	)
}

export const SuccessChip = (props: PropsWithChildren<ChipProps>) => {
	return (
		<StatusChip
			type={CHIP_TYPE.SUCCESS}
			showIcon
			customIconColor={'var(--txt-success)'}
			{...props}
		/>
	)
}

export const WarnChip = (props: PropsWithChildren<ChipProps>) => {
	return (
		<StatusChip
			type={CHIP_TYPE.WARN}
			showIcon
			customIconColor={'var(--txt-warning)'}
			{...props}
		/>
	)
}

export const ErrorChip = (props: PropsWithChildren<ChipProps>) => {
	return (
		<StatusChip
			type={CHIP_TYPE.ERROR}
			showIcon
			customIconColor={'var(--func-red-dark)'}
			{...props}
		/>
	)
}

export const BrandChip = (props: PropsWithChildren<ChipProps>) => {
	return (
		<StatusChip
			type={CHIP_TYPE.BRAND}
			{...props}
		/>
	)
}
